import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  User,
  signOut as originalSignOut,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  updateEmail,
} from "firebase/auth";
import { store } from "../redux/store";
import { cleanUser, loadUser } from "../redux/user";
import { app } from "../firebase/app";
import { analyticsSetUserId } from "./analytics";
import { updateProfile } from "./profile";

export interface GoogleUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

getAuth().onAuthStateChanged(async (user: GoogleUser | null) => {
  if (user) {
    store.dispatch(
      loadUser({ id: user?.uid, name: user?.displayName || "Anonymous" }),
    );
    analyticsSetUserId(user.uid);
  } else {
    store.dispatch(cleanUser());
  }
});

export function getCurrentUser(): User | null {
  return getAuth(app).currentUser;
}

export async function signInWithEmail(
  email: string,
  password: string,
  remember: boolean,
): Promise<User> {
  const auth = getAuth(app);
  await setPersistence(
    auth,
    remember ? browserLocalPersistence : browserSessionPersistence,
  );
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );
  return userCredential.user;
}

export async function signOut() {
  const auth = getAuth(app);
  await originalSignOut(auth);
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<User> {
  const auth = getAuth(app);
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );

  await updateProfile({ displayName });

  return userCredential.user;
}

export async function remindPassword(email: string): Promise<void> {
  const auth = getAuth(app);
  return await sendPasswordResetEmail(auth, email);
}

export async function updateAccountEmail(
  currentEmail: string,
  newEmail: string,
  password: string,
): Promise<void> {
  const auth = getAuth(app);
  if (!auth.currentUser) {
    throw "Not authorized";
  }
  await signInWithEmail(currentEmail, password, false);
  await updateEmail(auth.currentUser, newEmail);
}
